import { useState } from "react";
import LoadingExperience from "./LoadingExperience";
import { looksLikeStoreAddress } from "./RequestForm";
import usePersistedState from "../lib/usePersistedState";
import { trackEvent } from "../lib/analytics";

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content;
}

const inputClasses =
  "w-full rounded-xl border border-ink/15 bg-white px-4 py-3 outline-none transition focus:border-sky-deep focus:ring-4 focus:ring-sky-wash placeholder:text-ink/30";

function Field({ label, hint, error, children }) {
  return (
    <label className="block mb-4">
      <span className="block text-sm font-semibold mb-1.5">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-ink-soft">{hint}</span>}
      {error && <span className="mt-1 block text-sm text-red-600">{error}</span>}
    </label>
  );
}

export default function PartnerForm({ createPath, defaultLogoUrl, demoHost }) {
  const [domain, setDomain] = usePersistedState("partner.domain");
  const [email, setEmail] = usePersistedState("partner.email");
  const [partnerName, setPartnerName] = usePersistedState("partner.name");
  const [logoUrl, setLogoUrl] = usePersistedState("partner.logoUrl");
  const [clientName, setClientName] = usePersistedState("partner.clientName");
  const [message, setMessage] = usePersistedState("partner.message");
  const [slug, setSlug] = usePersistedState("partner.slug");
  const [brief, setBrief] = usePersistedState("partner.brief");
  const [logoBroken, setLogoBroken] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [statusPath, setStatusPath] = useState(null);

  const submit = async (event) => {
    event.preventDefault();
    if (!looksLikeStoreAddress(domain)) {
      setErrors({ myshopify_domain: ["Enter their .myshopify.com domain or their store's web address (like https://theirstore.com)."] });
      return;
    }
    setSubmitting(true);
    setErrors({});

    try {
      const response = await fetch(createPath, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify({
          domain,
          email,
          partner_name: partnerName,
          partner_logo_url: logoUrl,
          client_name: clientName,
          partner_message: message,
          slug,
          brief,
        }),
      });
      const data = await response.json();

      if (response.ok) {
        trackEvent("generate_lead", { form: "partner_demo_request" });
        if (data.status === "ready") {
          window.location.href = data.demo_path;
        } else {
          setStatusPath(`/d/${data.token}/status`);
        }
      } else {
        setErrors(data.errors || { base: ["Something went wrong. Please try again."] });
        setSubmitting(false);
      }
    } catch {
      setErrors({ base: ["We couldn't reach the server. Please try again."] });
      setSubmitting(false);
    }
  };

  if (statusPath) {
    return <LoadingExperience statusPath={statusPath} />;
  }

  const fieldErrors = (key) => (errors[key] || []).join(", ");
  const previewLogo = logoUrl.trim() && !logoBroken ? logoUrl.trim() : defaultLogoUrl;

  return (
    <form onSubmit={submit} className="text-left" noValidate>
      <Field label="Your client's Shopify store URL or myshopify domain" error={fieldErrors("myshopify_domain")}>
        <input
          type="text"
          required
          placeholder="https://theirstore.com or their-store.myshopify.com"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          className={inputClasses}
          autoComplete="off"
          spellCheck="false"
        />
      </Field>

      <Field label="Your work email" hint="The demo link lands here, not in your client's inbox." error={fieldErrors("email")}>
        <input type="email" required placeholder="you@agency.com" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClasses} />
      </Field>

      <div className="mb-4 mt-6 border-t border-ink/10 pt-5">
        <p className="text-sm font-semibold">Co-branding</p>
        <p className="mt-1 text-xs text-ink-soft">
          All optional. Anything left blank keeps the Simple Bundles default shown under each field.
        </p>
      </div>

      <Field label="Your agency or partner name" hint={'Default: "Simple Bundles".'} error={fieldErrors("partner_name")}>
        <input type="text" placeholder="e.g. DigitalJAS" value={partnerName} onChange={(e) => setPartnerName(e.target.value)} className={inputClasses} />
      </Field>

      <Field
        label="Your logo (image URL)"
        hint="Default: the Simple Bundles logo. Yours replaces it at the top of the demo; square or wide marks both fit."
        error={fieldErrors("partner_logo_url") || (logoBroken ? "We couldn't load that image URL, so the preview shows the default logo." : "")}
      >
        <input
          type="url"
          placeholder="https://your-site.com/logo.svg"
          value={logoUrl}
          onChange={(e) => {
            setLogoUrl(e.target.value);
            setLogoBroken(false);
          }}
          className={inputClasses}
          spellCheck="false"
        />
      </Field>

      <Field label="Client name" hint="Default: the store name we detect from their storefront. Shown as text next to your logo." error={fieldErrors("client_name")}>
        <input type="text" placeholder="e.g. Acme Supply" value={clientName} onChange={(e) => setClientName(e.target.value)} className={inputClasses} />
      </Field>

      <Field
        label="Opening message"
        hint="Default: no note. When you write one, it pops up on the demo as a small floating note from you, which your client can dismiss. Keep it short and personal."
        error={fieldErrors("partner_message")}
      >
        <textarea
          rows="3"
          placeholder={"e.g. Hi team, we built these bundle concepts from your live catalog. Click around and tell us your favourites."}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className={`${inputClasses} resize-y`}
        />
      </Field>

      <Field
        label="Custom link path"
        hint={`Default: a random code like /d/6SoIi7Wih7_r. Claim a readable one instead, so the link says who it's for before anyone clicks. First come, first served.`}
        error={fieldErrors("slug")}
      >
        <div className="flex items-center overflow-hidden rounded-xl border border-ink/15 bg-white transition focus-within:border-sky-deep focus-within:ring-4 focus-within:ring-sky-wash">
          <span className="shrink-0 select-none pl-4 text-ink/40">{demoHost}/d/</span>
          <input
            type="text"
            placeholder="glossier"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            className="w-full bg-transparent py-3 pl-0.5 pr-4 outline-none placeholder:text-ink/30"
            autoComplete="off"
            spellCheck="false"
          />
        </div>
      </Field>

      <Field
        label="Bundle brief"
        hint="Default: we read the catalog and pick the angles ourselves. Give us a direction instead and the bundles follow it. Changing your brief later redesigns the demo's bundles."
        error={fieldErrors("brief")}
      >
        <textarea
          rows="2"
          placeholder={"e.g. Gift sets under $50, lean into their bestsellers"}
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          className={`${inputClasses} resize-y`}
        />
      </Field>

      <div className="mb-5 rounded-xl border border-ink/10 bg-paper px-4 py-3">
        <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-ink-soft">Demo header preview</p>
        <div className="flex min-w-0 items-center gap-3">
          <img
            src={previewLogo}
            alt=""
            className="h-8 max-h-8 w-auto max-w-[9rem] shrink-0 object-contain object-left"
            onError={() => setLogoBroken(true)}
          />
          <span className="text-ink/25">×</span>
          <span className="truncate font-display text-lg font-bold">{clientName.trim() || "Client Store"}</span>
        </div>
      </div>

      {fieldErrors("base") && <p className="mb-4 text-sm text-red-600">{fieldErrors("base")}</p>}

      <button type="submit" className="btn-primary w-full text-base" disabled={submitting}>
        {submitting ? "Starting…" : "Create the co-branded demo"}
        {!submitting && <span aria-hidden="true">→</span>}
      </button>

      <p className="mt-4 text-center text-xs text-ink-soft">
        Free · Saved in your browser so you can come back and edit
      </p>
    </form>
  );
}
